import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import {
  type NotificationState,
  type NotificationUpdate,
  getNotificationState,
  updateNotifications,
} from "components/notifications/notification-actions";
import { useEligibleOffers } from "components/offers/hooks";
import {
  useKeystone,
  usePolicyId,
  useUiEvents,
  useUser,
} from "data/ss-data-hooks";
import { trackEvent, useFlags } from "utils";

export type NotificationDetail = {
  offerTitle: string;
  offerDescription: string;
  offerInternalName: string;
  path?: string;
  read: boolean;
  buttonText?: string;
};

export type NotificationItems = {
  notifications: NotificationDetail[];
  total: number;
  unread: number;
  isLoading: boolean;
  markRead: (notifications: NotificationDetail[]) => void;
  dismiss: (notifications: NotificationDetail[]) => void;
  openDrawer: () => void;
  closeDrawer: () => void;
  isDrawerOpen: boolean;
};

const offersDeclinedMap: Record<string, string> = {
  "wind-defense": "wind-defense-landing-declined",
  "ting-offer": "ting-landing-declined",
};

// Keys must match the offering internal name
const titleDescriptionMap: Record<
  string,
  { title: string; description: string; buttonText?: string; path?: string }
> = {
  "marketing-optin": {
    title: "Join our mailing list and get a free home maintenance checklist",
    description: `Get SageSure offers, news, and expert tips to protect your home, delivered to your inbox.`,
    path: "/my/account-notifications/marketing-preferences",
  },
  "wind-defense": {
    title: "Strengthen your roof for free with Wind Defense",
    description: `If your home is selected, a licensed technician will remove debris, clean gutters, and reseal shingles.`,
    buttonText: "Wind Defense",
    path: "/my/property-services/wind-defense",
  },
};

const toNotification = (
  offerInternalName: string,
  state: NotificationState,
  path?: string,
): NotificationDetail | null => {
  const content = titleDescriptionMap[offerInternalName];
  if (!content || state.dismissed.includes(offerInternalName)) {
    return null;
  }

  return {
    offerInternalName,
    offerTitle: content.title,
    offerDescription: content.description,
    buttonText: content.buttonText,
    path,
    read: state.read.includes(offerInternalName),
  };
};

const offerInternalNames = (notifications: NotificationDetail[]) =>
  notifications.map((notification) => notification.offerInternalName);

/**
 * The single owner of notification state: derives the notification list, writes
 * read/dismissed state, and controls the drawer.
 *
 * Call this exactly once, from `NotificationProvider`; consumers read the result
 * through `useNotifications`. A second caller would get its own copy of the
 * "navigating away closes the drawer" effect below, and on a single navigation
 * each copy would fire its own `markRead` PATCH and analytics event.
 */
export const useNotificationsController = (): NotificationItems => {
  const { pathname } = useLocation();
  const { notificationPilot } = useFlags();
  // The path the drawer was opened on, so that navigating away can close it.
  const [drawerPath, setDrawerPath] = useState<string | null>(null);

  const user = useUser();
  const selectedPolicyId = usePolicyId();
  const keystone = useKeystone();
  const {
    data: uiEvents,
    isLoading: uiEventsLoading,
    mutate: mutateUiEvents,
  } = useUiEvents();
  const { eligibleOffers, loading: offersLoading } = useEligibleOffers(
    notificationPilot ? selectedPolicyId : undefined,
  );

  const userId = user?.userId;
  const accountPreferences = user?.accountPreferences;
  const isLoading = uiEventsLoading || offersLoading;

  const update = useCallback(
    (notificationUpdate: NotificationUpdate) =>
      updateNotifications(
        { policyId: selectedPolicyId, userId, keystone, mutateUiEvents },
        notificationUpdate,
      ),
    [selectedPolicyId, userId, keystone, mutateUiEvents],
  );

  const markRead = useCallback(
    (notifications: NotificationDetail[]) =>
      update({ read: offerInternalNames(notifications) }),
    [update],
  );

  const dismiss = useCallback(
    (notifications: NotificationDetail[]) =>
      update({ dismissed: offerInternalNames(notifications) }),
    [update],
  );

  const notifications = useMemo(() => {
    if (!notificationPilot) {
      return [];
    }

    const state = getNotificationState(uiEvents, selectedPolicyId);
    const hasPreference = (preferenceName: string | undefined) => {
      return (
        !!preferenceName &&
        !!accountPreferences?.find(
          (pref) => pref.preferenceName === preferenceName,
        )?.preferenceValue
      );
    };

    return [
      ...eligibleOffers.unenrolledOffers.map((offer) =>
        hasPreference(offersDeclinedMap[offer.offeringInternalName])
          ? null
          : toNotification(offer.offeringInternalName, state, offer.offerUrl),
      ),
      hasPreference("marketing-optin")
        ? null
        : toNotification(
            "marketing-optin",
            state,
            titleDescriptionMap["marketing-optin"].path,
          ),
    ].filter((notification): notification is NotificationDetail =>
      Boolean(notification),
    );
  }, [
    notificationPilot,
    eligibleOffers.unenrolledOffers,
    uiEvents,
    selectedPolicyId,
    accountPreferences,
  ]);

  const openDrawer = useCallback(() => {
    trackEvent("Notification drawer opened");
    if (notifications.length > 0) {
      trackEvent("Notifications viewed", {
        offerInternalNames: notifications.map(
          (notification) => notification.offerInternalName,
        ),
        count: notifications.length,
      });
    }
    setDrawerPath(pathname);
  }, [notifications, pathname]);

  const closeDrawer = useCallback(() => {
    const unreadNotifications = notifications.filter(
      (notification) => !notification.read,
    );
    if (unreadNotifications.length) {
      trackEvent("Notification marked read", {
        offerInternalNames: unreadNotifications.map(
          (notification) => notification.offerInternalName,
        ),
      });
      markRead(unreadNotifications);
    }
    setDrawerPath(null);
  }, [notifications, markRead]);

  // Navigating away closes the drawer.
  useEffect(() => {
    if (drawerPath !== null && drawerPath !== pathname) {
      closeDrawer();
    }
  }, [pathname, drawerPath, closeDrawer]);

  const unread = isLoading
    ? 0
    : notifications.filter((notification) => !notification.read).length;

  const policyRef = useRef<string | undefined | null>(null);

  useEffect(() => {
    // unread changes up to twice after the initial load
    // (drawer close -> markRead -> unread settles to 0),
    // policyRef looks to prevent the trackEvent from re-firing since selectedPolicyId hasn't changed.
    // once we change the policyId & new notifications load then we will send another trackevent
    if (
      !notificationPilot ||
      isLoading ||
      policyRef.current === selectedPolicyId
    ) {
      return;
    }

    policyRef.current = selectedPolicyId;
    trackEvent("Notification Bell Shown", {
      policyId: selectedPolicyId,
      unread,
    });
  }, [notificationPilot, selectedPolicyId, isLoading, unread]);

  return {
    notifications,
    total: notifications.length,
    unread,
    isLoading,
    markRead,
    dismiss,
    openDrawer,
    closeDrawer,
    isDrawerOpen: drawerPath === pathname,
  };
};
